import React, { useState, useEffect, useCallback } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
} from 'react-router-dom';
import routes from '@/routes';
import loadablePage from '@/components/Loadable';
import { I18nextProvider } from 'react-i18next';
import I18n, { createI18n } from '@/core/i18n';
import { Provider } from 'react-redux';
import store from '@/store/index';
import Sentry from '@/utils/sentry';
import { getNativeMethod, pdfView } from './utils/misc';
import { needHandleBackCompability } from './utils/version';

const App: React.FC = () => {
  const [i18n, setI18n] = useState(I18n.isInitialized ? I18n : undefined);

  useEffect(
    useCallback(() => {
      if (!I18n.isInitialized) {
        createI18n().then((i18nInstance) => setI18n(i18nInstance));
      } else if (!i18n) {
        setI18n(I18n);
      }
    }, [i18n]),
    [],
  );

  const handleClick = (event) => {
    const target = event.target as HTMLLinkElement;
    if (target?.nodeName.toLocaleLowerCase() === 'a') {
      let url = target.getAttribute('href-help-center');
      if (url != undefined) {
        target.setAttribute('href', url);
      }
      url = target.getAttribute('href');

      if (!url) return;

      if (needHandleBackCompability(url, event)) return;

      // external pdf link open
      if (url.includes('.pdf')) {
        pdfView(url);
        event.preventDefault();
        return;
      }

      if (url.startsWith('native:')) {
        const res = getNativeMethod(url);
        (window as MyWindow).WebViewJavascriptBridge.callHandler(
          res.method,
          res.params,
        );
        event.preventDefault();
        return;
      }
    }
  };

  return (
    <Sentry.ErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <Provider store={store}>
          <Router basename={process.env.BASE_URL}>
            <div onClick={handleClick}>
              <Switch>
                {routes.map(({ page, redirect, ...otherProps }) => {
                  const component = loadablePage(page);
                  if (redirect) {
                    return <Redirect key={page} to={redirect} />;
                  }
                  return (
                    <Route key={page} component={component} {...otherProps} />
                  );
                })}
              </Switch>
            </div>
          </Router>
        </Provider>
      </I18nextProvider>
    </Sentry.ErrorBoundary>
  );
};

export default App;
